import React, { useState, useEffect, useCallback } from "react";

import FlagSprite from "./FlagSprite";
import Icon from "./Icon";
import Loading from "./Loading";

// Goal-driven onboarding: Goal → Level → (Grammar/Topic picker) → Skill → Personalized recommendation.
// Pulls collections from the language pairing API and adjusts available options dynamically.

// --- Recommendation Engine ---
function getRecommendation(answers, collections, isListeningAvailable) {
  const { goal, level, grammarTopic, topicChoice, skillPref } = answers;
  let collection = null;
  let skill = "vocabulary";
  let mode = "multiple_choice";

  const hasType = (t) => collections.some((c) => c.type === t);
  const getByType = (t) => collections.filter((c) => c.type === t);

  // Determine skill & mode from preference
  if (skillPref === "reading") {
    skill = "vocabulary";
    mode = level === "beginner" ? "multiple_choice" : "text_input";
  } else if (skillPref === "listening" && isListeningAvailable) {
    skill = "listening";
    mode = level === "beginner" ? "multiple_choice" : "full_text_input";
  } else if (skillPref === "speaking") {
    skill = "speaking";
    mode = "speaking";
  } else {
    skill = "vocabulary";
    mode = level === "beginner" ? "multiple_choice" : "multiple_choice";
  }

  // Determine collection based on goal
  if (goal === "grammar" && grammarTopic) {
    const grammar = getByType("GrammarCollection");
    collection = grammar.find((c) => c.name === grammarTopic);
    if (!collection) collection = grammar[0];
    // Grammar works best with vocab skill
    skill = "vocabulary";
    mode = level === "beginner" ? "multiple_choice" : "text_input";
  } else if (goal === "topic" && topicChoice) {
    const topics = getByType("TopicCollection");
    collection = topics.find((c) => c.name === topicChoice);
    if (!collection) collection = topics[0];
  } else if (goal === "structured") {
    if (hasType("CefrCollection")) {
      const cefr = getByType("CefrCollection");
      const cefrMap = {
        beginner: "Beginner A1",
        elementary: "Advanced Beginner A2",
        intermediate: "Intermediate B1",
        advanced: "Upper Intermediate B2",
      };
      const target = cefrMap[level] || "Beginner A1";
      collection = cefr.find((c) => c.name === target) || cefr[0];
    }
  } else if (goal === "vocabulary") {
    if (hasType("FastTrackV2Collection")) {
      const ft = getByType("FastTrackV2Collection");
      const ftMap = {
        beginner: "Fast Track Level 1",
        elementary: "Fast Track Level 2",
        intermediate: "Fast Track Level 4",
        advanced: "Fast Track Level 7",
      };
      const target = ftMap[level] || "Fast Track Level 1";
      collection = ft.find((c) => c.name === target) || ft[0];
    } else if (hasType("FrequencyCollection")) {
      const freq = getByType("FrequencyCollection");
      const fMap = {
        beginner: "500 Most Common",
        elementary: "1,000 Most Common",
        intermediate: "3,000 Most Common",
        advanced: "10,000 Most Common",
      };
      const target = fMap[level] || "1,000 Most Common";
      collection = freq.find((c) => c.name === target) || freq[0];
    }
  } else if (goal === "essentials") {
    if (hasType("Core1kCollection")) {
      collection = getByType("Core1kCollection")[0];
    } else if (hasType("FastTrackV2Collection")) {
      collection = getByType("FastTrackV2Collection").find(
        (c) => c.name === "Fast Track Level 1"
      );
    }
    skill = "vocabulary";
    mode = "multiple_choice";
  }

  // Fallback
  if (!collection) {
    if (hasType("FastTrackV2Collection")) {
      collection = getByType("FastTrackV2Collection").find(
        (c) => c.name === "Fast Track Level 1"
      );
    } else if (hasType("FrequencyCollection")) {
      collection = getByType("FrequencyCollection").find(
        (c) => c.name === "1,000 Most Common"
      );
    }
    if (!collection) {
      collection = collections[0];
    }
  }

  // Build a human-readable reason for the recommendation
  const skillLabel = skill === "listening" ? "listening" : skill === "speaking" ? "speaking" : "reading";
  const modeLabel = mode === "multiple_choice" ? "multiple choice" : mode === "text_input" ? "typing the answer" : mode === "transcribe" ? "transcribing what you hear" : "speaking out loud";
  let reason = "";
  if (goal === "essentials") {
    reason = "We picked the most useful everyday sentences to get you started quickly. You'll practice by " + modeLabel + " — low pressure, high reward.";
  } else if (goal === "vocabulary") {
    reason = "You'll learn words in order of how frequently they're used, starting at your level. Practice by " + modeLabel + " to build vocabulary fast.";
  } else if (goal === "structured") {
    reason = "This follows the CEFR framework used by language schools worldwide, matched to your current level. You'll work through themed units by " + modeLabel + ".";
  } else if (goal === "grammar") {
    reason = "Focused practice on " + (grammarTopic || "grammar") + " in real sentences. You'll see each pattern in context and " + (mode === "text_input" ? "type the answer from memory." : "choose the correct form.");
  } else if (goal === "topic") {
    reason = "Real sentences about " + (topicChoice || "your chosen topic").toLowerCase() + " so you can " + skillLabel + " what matters to you most. Practice by " + modeLabel + ".";
  } else {
    reason = "A collection matched to your level. You'll practice by " + modeLabel + ".";
  }

  return { collection, skill, mode, reason };
}

function buildPlayUrl(collection, skill, mode) {
  if (!collection || !collection.playWebUrl) return null;
  const params = new URLSearchParams();
  params.set("skill", skill);
  params.set("mode", mode);
  params.set("onboarding", "true");
  return collection.playWebUrl + "?" + params.toString();
}

// --- Theme ---
// V5 CSS variables only have .v5-light definitions; no .v5-dark exists.
// Detect body.dark-mode and return appropriate palette.
function useTheme() {
  const [dark, setDark] = useState(
    () => document.body.classList.contains("dark-mode")
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setDark(document.body.classList.contains("dark-mode"));
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  return dark
    ? {
        bg: "#121212",
        bgCard: "#1e1e1e",
        bgElevated: "#2a2a2a",
        green: "#5cb85c",
        greenHover: "#4cae4c",
        greenMuted: "rgba(92, 184, 92, 0.15)",
        text: "rgba(255, 255, 255, 0.87)",
        textSecondary: "#aaa",
        textMuted: "#777",
        border: "#444",
        radius: 12,
        shadow: "0 1px 3px rgba(0,0,0,0.2)",
        selectedShadow: "0 0 0 1px #5cb85c",
      }
    : {
        bg: "#f5f5f5",
        bgCard: "#ffffff",
        bgElevated: "#f0f0f0",
        green: "#5cb85c",
        greenHover: "#4cae4c",
        greenMuted: "rgba(92, 184, 92, 0.1)",
        text: "#1a1a1a",
        textSecondary: "#555",
        textMuted: "#888",
        border: "#ddd",
        radius: 12,
        shadow: "0 1px 3px rgba(0,0,0,0.04)",
        selectedShadow: "0 0 0 1px #5cb85c",
      };
}

// --- UI Components ---

function ProgressDots({ current, total, theme }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        justifyContent: "center",
        marginBottom: 30,
      }}
    >
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: i <= current ? theme.green : theme.border,
            transition: "background 0.3s",
          }}
        />
      ))}
    </div>
  );
}

function OptionCard({ icon, title, subtitle, selected, onClick, compact, theme }) {
  return (
    <button
      onClick={onClick}
      type="button"
      style={{
        display: "flex",
        alignItems: "center",
        gap: compact ? 12 : 14,
        padding: compact ? "12px 16px" : "14px 18px",
        border: "1px solid",
        borderColor: selected ? theme.green : theme.border,
        borderRadius: theme.radius,
        background: selected ? theme.greenMuted : theme.bgCard,
        boxShadow: selected ? theme.selectedShadow : theme.shadow,
        cursor: "pointer",
        width: "100%",
        textAlign: "left",
        transition: "border-color 0.2s, background 0.2s, box-shadow 0.2s",
        color: theme.text,
        outline: "none",
      }}
    >
      {icon && (
        <span style={{ fontSize: compact ? 20 : 22, flexShrink: 0 }}>
          {icon}
        </span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: compact ? 14 : 15,
            fontWeight: selected ? 700 : 500,
            color: theme.text,
          }}
        >
          {title}
        </div>
        {subtitle && (
          <div
            style={{
              fontSize: 13,
              color: theme.textSecondary,
              marginTop: 2,
              lineHeight: 1.3,
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
      {selected && (
        <Icon
          name="ok"
          style={{ color: theme.green, fontSize: 14, flexShrink: 0 }}
        />
      )}
    </button>
  );
}

function StepContainer({
  title,
  subtitle,
  children,
  onBack,
  onNext,
  nextLabel,
  nextDisabled,
  animate,
  theme,
}) {
  return (
    <div
      style={{
        opacity: animate ? 1 : 0,
        transform: animate ? "translateY(0)" : "translateY(12px)",
        transition: "all 0.35s ease",
      }}
    >
      <div style={{ marginBottom: 20 }}>
        <h3
          style={{
            fontWeight: 700,
            fontSize: 22,
            marginBottom: 6,
            color: theme.text,
          }}
        >
          {title}
        </h3>
        {subtitle && (
          <p
            style={{
              fontSize: 15,
              color: theme.textSecondary,
              margin: "6px 0 0",
              lineHeight: 1.4,
            }}
          >
            {subtitle}
          </p>
        )}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          marginBottom: 24,
        }}
      >
        {children}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {onNext && (
          <button
            onClick={onNext}
            disabled={nextDisabled}
            type="button"
            className="btn btn-success joystix btn-block btn-lg"
          >
            {nextLabel || "Continue"}
          </button>
        )}
        {onBack && (
          <button
            onClick={onBack}
            type="button"
            className="btn btn-default btn-sm joystix"
            style={{ alignSelf: "center" }}
          >
            <Icon name="chevron-left" /> Back
          </button>
        )}
      </div>
    </div>
  );
}

// --- Main Component ---
export default function V5Onboarding3(props) {
  const {
    languagePairingUrl,
    targetLanguageIso,
    targetLanguageCode,
    targetLanguageName,
    targetLanguageFlagIso,
    baseLanguageName,
    dashboardUrl,
    isSpeakingSkillAvailable,
    onboardingV5Url,
    onSkip,
  } = props;

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    goal: null,
    level: null,
    grammarTopic: null,
    topicChoice: null,
    skillPref: null,
  });
  const [animate, setAnimate] = useState(true);
  const [result, setResult] = useState(null);
  const [collections, setCollections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isListeningAvailable, setIsListeningAvailable] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    loadCollections();
    window.clozemaster.systemTtsAvailable(
      targetLanguageIso,
      targetLanguageCode
    );
  }, []);

  function loadCollections() {
    $.ajax({
      data: {
        include_cefr: true,
        include_fast_track_v2_collections: true,
      },
      url: languagePairingUrl,
    })
      .done((data) => {
        const { collections: colls } = data;
        setCollections(colls);
        setLoading(false);
        setIsListeningAvailable(
          colls.some((c) => c.ttsAvailable) ||
            window.clozemaster.systemTtsAvailable(
              targetLanguageIso,
              targetLanguageCode
            )
        );
      })
      .fail(() => {
        setLoading(false);
        setError("Failed to load. Please refresh and try again.");
      });
  }

  const hasType = (t) => collections && collections.some((c) => c.type === t);
  const getByType = (t) =>
    collections ? collections.filter((c) => c.type === t) : [];

  const transition = useCallback((nextStep) => {
    setAnimate(false);
    setTimeout(() => {
      setStep(nextStep);
      setAnimate(true);
    }, 180);
  }, []);

  const setAnswer = (key, val) => {
    setAnswers((prev) => ({ ...prev, [key]: val }));
    if (onboardingV5Url) {
      $.ajax({
        method: "PUT",
        url: onboardingV5Url,
        data: { responses: { [key]: val } }
      });
    }
  };

  // Conditional steps
  const needsGrammarOrTopic =
    answers.goal === "grammar" || answers.goal === "topic";
  const needsSkill = answers.goal !== "essentials";

  // Steps: 0=goal, 1=level, 2=grammar/topic, 3=skill, 4=result
  const getNextStep = (current) => {
    if (current === 0) return 1;
    if (current === 1) {
      if (needsGrammarOrTopic) return 2;
      if (needsSkill) return 3;
      return 4;
    }
    if (current === 2) return needsSkill ? 3 : 4;
    if (current === 3) return 4;
    return current + 1;
  };

  const getPrevStep = (current) => {
    if (current === 4) {
      if (needsSkill) return 3;
      if (needsGrammarOrTopic) return 2;
      return 1;
    }
    if (current === 3) return needsGrammarOrTopic ? 2 : 1;
    if (current === 2) return 1;
    if (current === 1) return 0;
    return 0;
  };

  const totalDots =
    2 + (needsGrammarOrTopic ? 1 : 0) + (needsSkill ? 1 : 0);
  const dotIndex = (() => {
    if (step <= 0) return 0;
    if (step === 1) return 1;
    if (step === 2) return 2;
    if (step === 3) return needsGrammarOrTopic ? 3 : 2;
    return totalDots;
  })();

  const goNext = () => {
    const next = getNextStep(step);
    if (next === 4) {
      const rec = getRecommendation(answers, collections, isListeningAvailable);
      setResult(rec);
    }
    transition(next);
  };
  const goBack = () => transition(getPrevStep(step));

  // --- Loading / Error ---
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <div className="text-center">
          <p style={{ color: "#d9534f" }}>{error}</p>
          <button
            style={{
              background: theme.bgCard,
              border: "1px solid " + theme.border,
              borderRadius: theme.radius,
              padding: "8px 16px",
              cursor: "pointer",
              color: theme.text,
            }}
            onClick={() => {
              setLoading(true);
              setError(null);
              loadCollections();
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // --- Header ---
  const header = (
    <div className="text-center" style={{ marginBottom: 28 }}>
      {targetLanguageFlagIso && (
        <FlagSprite iso={targetLanguageFlagIso} size={48} />
      )}
      <h2
        style={{
          fontWeight: 700,
          fontSize: 24,
          marginTop: 10,
          marginBottom: 4,
          color: theme.text,
        }}
      >
        {targetLanguageName}
      </h2>
      {baseLanguageName && (
        <div style={{ fontSize: 14, color: theme.textSecondary }}>
          from {baseLanguageName}
        </div>
      )}
    </div>
  );

  const wrap = (children) => (
    <div
      style={{
        display: "flex",
        flex: 1,
        justifyContent: "center",
        alignItems: "flex-start",
        padding: "60px 20px 40px",
      }}
    >
      <div style={{ width: "100%", maxWidth: 460 }}>
        {header}
        <ProgressDots current={dotIndex} total={totalDots} theme={theme} />
        {children}
      </div>
    </div>
  );

  // --- Step 0: Goal ---
  if (step === 0) {
    const goals = [
      {
        id: "essentials",
        icon: "🚀",
        title: "Just get me started",
        sub: "Jump into the most useful sentences right away",
      },
      {
        id: "vocabulary",
        icon: "📚",
        title: "Build my vocabulary",
        sub: "Work through words by frequency, most common first",
      },
      {
        id: "structured",
        icon: "🎓",
        title: "Follow a structured path",
        sub: "Study by CEFR level (A1 → B2) with themed units",
      },
      {
        id: "grammar",
        icon: "🔧",
        title: "Fix a grammar weak spot",
        sub: "Drill a specific grammar point in context",
      },
      {
        id: "topic",
        icon: "🗺️",
        title: "Learn for a specific situation",
        sub: "Travel, work, slang, food, and more",
      },
    ];
    const available = goals.filter((g) => {
      if (g.id === "grammar" && !hasType("GrammarCollection")) return false;
      if (g.id === "structured" && !hasType("CefrCollection")) return false;
      if (g.id === "topic" && !hasType("TopicCollection")) return false;
      if (
        g.id === "essentials" &&
        !hasType("Core1kCollection") &&
        !hasType("FastTrackV2Collection")
      )
        return false;
      if (
        g.id === "vocabulary" &&
        !hasType("FastTrackV2Collection") &&
        !hasType("FrequencyCollection")
      )
        return false;
      return true;
    });

    return wrap(
      <StepContainer
        title="What brings you here?"
        subtitle="We'll match you with the perfect collection."
        onNext={goNext}
        nextDisabled={!answers.goal}
        animate={animate}
        theme={theme}
      >
        {available.map((g) => (
          <OptionCard
            key={g.id}
            icon={g.icon}
            title={g.title}
            subtitle={g.sub}
            selected={answers.goal === g.id}
            onClick={() => setAnswer("goal", g.id)}
            theme={theme}
          />
        ))}
      </StepContainer>
    );
  }

  // --- Step 1: Level ---
  if (step === 1) {
    const levels = [
      {
        id: "beginner",
        icon: "🌱",
        title: "Just starting out",
        sub: "I know a few words or phrases",
      },
      {
        id: "elementary",
        icon: "🌿",
        title: "I know the basics",
        sub: "I can handle simple conversations",
      },
      {
        id: "intermediate",
        icon: "🌳",
        title: "Intermediate",
        sub: "I can read and follow most everyday content",
      },
      {
        id: "advanced",
        icon: "🏔️",
        title: "Advanced",
        sub: "I'm comfortable but want to sharpen up",
      },
    ];
    return wrap(
      <StepContainer
        title="How's your level?"
        subtitle="Be honest — we'll place you where you'll learn the most."
        onBack={goBack}
        onNext={goNext}
        nextDisabled={!answers.level}
        animate={animate}
        theme={theme}
      >
        {levels.map((l) => (
          <OptionCard
            key={l.id}
            icon={l.icon}
            title={l.title}
            subtitle={l.sub}
            selected={answers.level === l.id}
            onClick={() => setAnswer("level", l.id)}
            theme={theme}
          />
        ))}
      </StepContainer>
    );
  }

  // --- Step 2: Grammar or Topic picker ---
  if (step === 2) {
    if (answers.goal === "grammar") {
      const grammars = getByType("GrammarCollection");
      return wrap(
        <StepContainer
          title="Which grammar point?"
          subtitle="Pick the one that trips you up the most."
          onBack={goBack}
          onNext={goNext}
          nextDisabled={!answers.grammarTopic}
          animate={animate}
          theme={theme}
        >
          <div
            style={{
              // maxHeight: 320,
              // overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              paddingRight: 4,
            }}
          >
            {grammars.map((g) => (
              <OptionCard
                key={g.name}
                title={g.name}
                selected={answers.grammarTopic === g.name}
                onClick={() => setAnswer("grammarTopic", g.name)}
                compact
                theme={theme}
              />
            ))}
          </div>
        </StepContainer>
      );
    }
    if (answers.goal === "topic") {
      const topics = getByType("TopicCollection");
      return wrap(
        <StepContainer
          title="What's your focus?"
          subtitle="Pick the situation that matters most to you right now."
          onBack={goBack}
          onNext={goNext}
          nextDisabled={!answers.topicChoice}
          animate={animate}
          theme={theme}
        >
          <div
            style={{
              // maxHeight: 320,
              // overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              paddingRight: 4,
            }}
          >
            {topics.map((t) => (
              <OptionCard
                key={t.name}
                title={t.name}
                subtitle={t.description || null}
                selected={answers.topicChoice === t.name}
                onClick={() => setAnswer("topicChoice", t.name)}
                compact
                theme={theme}
              />
            ))}
          </div>
        </StepContainer>
      );
    }
    // Shouldn't reach here, skip forward
    goNext();
    return null;
  }

  // --- Step 3: Skill preference ---
  if (step === 3) {
    const skills = [
      {
        id: "reading",
        icon: "eye-open",
        title: "Reading & vocabulary",
        sub: "See sentences, fill in the blank",
      },
      {
        id: "listening",
        icon: "headphones",
        title: "Listening",
        sub: "Hear the sentence, figure out the word",
        available: isListeningAvailable,
      },
      {
        id: "speaking",
        icon: "comment",
        title: "Speaking",
        sub: "Say the answer out loud",
        available: isSpeakingSkillAvailable
        || (window.clozemaster.speechRecognition.isSupported(targetLanguageIso)
            && window.clozemaster.speechRecognition.isAvailable()),
      },
    ];
    const availableSkills = skills.filter((s) => s.available !== false);

    return wrap(
      <StepContainer
        title="How do you want to practice?"
        subtitle="You can always switch later — pick what feels right."
        onBack={goBack}
        onNext={goNext}
        nextDisabled={!answers.skillPref}
        animate={animate}
        theme={theme}
      >
        {availableSkills.map((s) => (
          <OptionCard
            key={s.id}
            icon={<Icon name={s.icon} />}
            title={s.title}
            subtitle={s.sub}
            selected={answers.skillPref === s.id}
            onClick={() => setAnswer("skillPref", s.id)}
            theme={theme}
          />
        ))}
      </StepContainer>
    );
  }

  // --- Step 4: Result ---
  if (step === 4 && result && result.collection) {
    const playUrl = buildPlayUrl(result.collection, result.skill, result.mode);
    const modeLabels = {
      multiple_choice: "Multiple Choice",
      text_input: "Text Input",
      full_text_input: "Transcribe",
      speaking: "Speaking",
    };
    const skillLabels = {
      vocabulary: "Vocabulary",
      listening: "Listening",
      speaking: "Speaking",
    };
    const typeLabels = {
      CefrCollection: "CEFR Leveled",
      FastTrackV2Collection: "Fast Track",
      FrequencyCollection: "Frequency",
      GrammarCollection: "Grammar",
      TopicCollection: "Topic",
      Core1kCollection: "Essentials",
      IdiomsCollection: "Idioms",
    };

    return (
      <div
        style={{
          display: "flex",
          flex: 1,
          justifyContent: "center",
          alignItems: "flex-start",
          padding: "60px 20px 40px",
        }}
      >
        <div style={{ width: "100%", maxWidth: 460 }}>
          {header}
          <div
            style={{
              opacity: animate ? 1 : 0,
              transform: animate ? "translateY(0)" : "translateY(16px)",
              transition: "all 0.4s ease",
            }}
          >
            <div className="text-center" style={{ marginBottom: 24 }}>
              <h3
                style={{
                  fontWeight: 700,
                  fontSize: 22,
                  marginBottom: 8,
                  color: theme.text,
                }}
              >
                You're all set!
              </h3>
            </div>

            {/* Recommendation card */}
            <div
              style={{
                background: theme.bgCard,
                border: "1px solid " + theme.border,
                borderRadius: theme.radius,
                padding: "20px",
                marginBottom: 16,
                boxShadow: theme.shadow,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  color: theme.green,
                  marginBottom: 10,
                }}
              >
                Recommended collection
              </div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 18,
                  color: theme.text,
                  marginBottom: 6,
                }}
              >
                {result.collection.name}
              </div>
              {result.reason && (
                <div
                  style={{
                    fontSize: 14,
                    color: theme.textSecondary,
                    lineHeight: 1.5,
                    marginBottom: 12,
                  }}
                >
                  {result.reason}
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    padding: "4px 10px",
                    borderRadius: 6,
                    background: theme.greenMuted,
                    fontSize: 12,
                    fontWeight: 600,
                    color: theme.green,
                  }}
                >
                  {skillLabels[result.skill]}
                </span>
                {result.mode !== result.skill && (
                  <span
                    style={{
                      display: "inline-block",
                      padding: "4px 10px",
                      borderRadius: 6,
                      background: theme.greenMuted,
                      fontSize: 12,
                      fontWeight: 600,
                      color: theme.green,
                    }}
                  >
                    {modeLabels[result.mode]}
                  </span>
                )}
                {result.collection.numSentences > 0 && (
                  <span
                    style={{
                      fontSize: 12,
                      color: theme.textMuted,
                      marginLeft: 4,
                    }}
                  >
                    {result.collection.numSentences.toLocaleString()} sentences
                  </span>
                )}
              </div>
            </div>

            {/* CTA */}
            <a
              href={playUrl}
              className="btn btn-success joystix btn-block btn-lg"
              style={{ textDecoration: "none" }}
            >
              Start Playing{" "}
              <span className="glyphicon glyphicon-chevron-right"></span>
            </a>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 12,
                marginTop: 12,
              }}
            >
              <button
                onClick={() => {
                  setResult(null);
                  setAnswers({
                    goal: null,
                    level: null,
                    grammarTopic: null,
                    topicChoice: null,
                    skillPref: null,
                  });
                  transition(0);
                }}
                type="button"
                className="btn btn-default btn-sm joystix"
              >
                Start over
              </button>
              <a
                href={(dashboardUrl || "#") + "?tab=collections"}
                style={{
                  color: theme.textMuted,
                  fontSize: 14,
                  textDecoration: "none",
                }}
              >
                or explore all collections
              </a>
            </div>

            <p
              style={{
                fontSize: 12,
                color: theme.textMuted,
                textAlign: "center",
                marginTop: 16,
                lineHeight: 1.4,
              }}
            >
              You can change your collection and play options anytime from the{" "}
              <a
                href={dashboardUrl || "#"}
                style={{ color: theme.textMuted, textDecoration: "underline" }}
              >
                dashboard
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
